import { Switch } from '@headlessui/react'
import config from 'config'
import { HelpCircle, Play } from 'lucide-react'
import { useContext, useRef, useState } from 'react'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { RxCross2 } from 'react-icons/rx'
import { toast } from 'react-toastify'
import { useReactFlow } from 'reactflow'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'
import { getOrgId } from 'utils/funcs/browser/getOrgId'

import FlowContext from '../../contextFlow'
// Local components
import ConditionOptions from '../conditionOptions'
import ExtractInfo from '../extractInfo'

function SchedulingModal({ id, setIsOpen, data }) {
  const { getNodes, getEdges } = useReactFlow()
  const { triggerUpdate, elements } = useContext(FlowContext)
  const orgId = getOrgId()
  const textareaRef = useRef(null)
  let isWarnToast = false

  // Basic node properties
  const [name, setName] = useState(data.name || 'Get Available Slots')

  // Response variables that must be parsed from the API response
  const DEFAULT_RESPONSE_VARS = [
    { name: 'available_slots', data: '', context: 'Must contain a flat array of dates in ISO format' },
  ]

  // Webhook configuration
  const [isExpanded, setIsExpanded] = useState(true)
  const [url, setURL] = useState(data.url || '')
  const [method, setMethod] = useState(data.method || 'POST')
  const [headers, setHeaders] = useState(data.headers || [['Content-Type', 'application/json']])
  const [body, setBody] = useState(data.body || JSON.stringify({
    start_datetime: '{{start_datetime}}',
  }, null, 2))

  // Extract variables state
  const [extractVars, setExtractVars] = useState(
    data.extractVars || [
      ['start_datetime', 'string', 'ISO format date (required)', false],
    ],
  )
  const [useExtractVars, setUseExtractVars] = useState(true)
  const [extractVarSettings, setExtractVarSettings] = useState(
    data.extractVarSettings || {},
  )
  const [variableValidationErrors, setVariableValidationErrors] = useState([])

  // Response data
  const [responseData, setResponseData] = useState(
    data.responseData || DEFAULT_RESPONSE_VARS,
  )
  const [useResponseData, setUseResponseData] = useState(true)

  // API testing state
  const [apiResponse, setApiResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const hardcodedPrefix = 'Wait until the user has given a valid time and the agent has confirmed the time. The agent must explicitly repeat the date and time back to the user, and the user must agree that they want to confirm the appointment for that date and time. '
  const [condition, setCondition] = useState(
    data.condition || '',
  )

  // Additional config
  const [timeoutValue, setTimeoutValue] = useState(data.timeoutValue || 10)
  const [useReroute, setUseReroute] = useState(data.useReroute !== false)

  // Helpers
  const addHeader = () => {
    setHeaders([...headers, ['', '']])
  }

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index))
  }

  const updateHeader = (index, key, value) => {
    const updatedHeaders = headers.map((header, i) => {
      if (i === index) {
        return [key, value]
      }
      return header
    })
    setHeaders(updatedHeaders)
  }

  const validateVariables = () => {
    if (!useExtractVars) {
      return true
    }
    const errors = []
    extractVars.forEach((variable, index) => {
      variable.forEach((field, fieldIndex) => {
        if (typeof field === 'string' && field.trim() === '') {
          errors.push({
            index,
            field: fieldIndex,
            message: 'Field is required',
          })
        }
      })
    })
    setVariableValidationErrors(errors)
    return errors.length === 0
  }

  const addResponseData = () => {
    setResponseData([...responseData, { name: '', data: '', context: '' }])
  }

  const removeResponseData = (index) => {
    setResponseData(responseData.filter((_, i) => i !== index))
  }

  const updateResponseData = (index, field, value) => {
    const updatedData = responseData.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value }
      }
      return item
    })
    setResponseData(updatedData)
  }

  // API Testing
  const testApiRequest = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const headerObj = Object.fromEntries(headers)
      // Delete empty keys
      Object.keys(headerObj).forEach((key) => {
        if (headerObj[key] === '') {
          delete headerObj[key]
        }
      })

      // Prepare body with variable values
      let processedBody = body
      extractVars.forEach((variable) => {
        if (variable[0]) {
          const regex = new RegExp(`{{${variable[0]}}}`, 'g')
          processedBody = processedBody.replace(regex, variable[1])
        }
      })

      if (useReroute) {
        // Rerouted version through server
        const runEndpointUrl = `${config.API_URL}/tools/run`
        const response = await fetch(runEndpointUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthToken(),
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
          body: JSON.stringify({
            url,
            method,
            headers: headerObj,
            body: processedBody,
          }),
        })

        const data = await response.json()

        if (data.data?.statusCode >= 200 && data.data?.statusCode < 300) {
          setApiResponse(data.data?.responseData)
        }
        else {
          throw new Error(
            `Request failed with status ${data.data?.statusCode}`,
          )
        }
      }
      else {
        // Direct version
        const response = await fetch(url, {
          method,
          headers: headerObj,
          body: processedBody,
        })
        const data = await response.json()
        setApiResponse(data)
      }
    }
    catch (err) {
      setError(err.message)
    }
    finally {
      setIsLoading(false)
    }
  }

  // Variable handling
  const handleVariableChange = (key, value) => {
    setExtractVars(extractVars.map((variable) => {
      if (variable[0] === key) {
        // Preserve the other values (description and flags)
        return [key, value, variable[2], variable[3], variable[4]]
      }
      return variable
    }))
  }

  // Render JSON with extract buttons
  const renderJsonWithExtract = (obj, path = '$', depth = 0) => {
    const indent = depth * 2

    if (Array.isArray(obj)) {
      return (
        <div style={{ marginLeft: `${indent}px` }} className="my-0.5">
          [
          <div style={{ marginLeft: '20px' }}>
            {obj.map((item, index) => (
              <div key={index} className="my-0.5">
                {renderJsonWithExtract(item, `${path}[${index}]`, depth + 1)}
              </div>
            ))}
          </div>
          ]
        </div>
      )
    }
    else if (typeof obj === 'object' && obj !== null) {
      return (
        <div style={{ marginLeft: `${indent}px` }} className="my-0.5">
          {'{'}
          <div style={{ marginLeft: '20px' }}>
            {Object.entries(obj).map(([key, value]) => (
              <div key={key} className="my-0.5 flex items-start">
                <button
                  onClick={() => {
                    setUseResponseData(true)
                    addToResponseData({
                      name: key,
                      path: `${path}.${key}`,
                      value: JSON.stringify(value),
                    })
                  }}
                  className={`
                    mr-1.5 shrink-0 rounded bg-indigo-100 p-0.5 text-2xs
                    text-indigo-700 transition-colors duration-200
                    hover:bg-indigo-200
                  `}
                  title="Extract this value"
                >
                  +
                </button>
                <span className="mr-1.5 font-semibold text-purple-700">
                  "
                  {key}
                  ":
                </span>
                <div className="grow">
                  {renderJsonWithExtract(value, `${path}.${key}`, depth + 1)}
                </div>
              </div>
            ))}
          </div>
          {'}'}
        </div>
      )
    }
    else if (typeof obj === 'string') {
      return (
        <span className="text-green-600">
          "
          {obj}
          "
        </span>
      )
    }
    else if (typeof obj === 'number') {
      return <span className="text-blue-600">{obj}</span>
    }
    else if (typeof obj === 'boolean') {
      return <span className="text-red-600">{obj.toString()}</span>
    }
    else if (obj === null) {
      return <span className="text-gray-500">null</span>
    }
    else {
      return <span>{obj}</span>
    }
  }

  const addToResponseData = (item) => {
    setResponseData([
      ...responseData,
      { name: item.name, data: item.path, context: '' },
    ])
    toast.success('Value extracted and added to response data.')
  }

  const handleSave = async () => {
    if (validateVariables()) {
      const updatedEdges = elements.edges
      const updatedNodes = elements.nodes.map((element_node) => {
        if (element_node.id === id) {
          const node = structuredClone(element_node)

          // Update node data
          node.data.name = name
          node.type = 'Scheduling'

          // Add properties with expected naming for SchedulingNode
          node.data.schedulingToolUrl = url
          node.data.schedulingToolMethod = method
          node.data.schedulingToolBody = body
          node.data.timeoutValue = timeoutValue
          node.data.useReroute = useReroute

          // Add headers with expected naming if any
          const nonEmptyHeaders = headers.filter(
            header => header[0] !== '' || header[1] !== '',
          )
          if (nonEmptyHeaders.length > 0) {
            node.data.schedulingToolHeaders = nonEmptyHeaders
          }

          // Always include extract vars - ensure they have the correct format
          node.data.useExtractVars = true
          const requiredVars = extractVars.filter((varArr) => {
            // For all variables, ensure name field is non-empty
            return varArr[0] !== ''
          }).map((varArr) => {
            // Ensure each variable has the expected format [name, type, description, increaseSpellingAccuracy]
            const [name, varType, description, increaseSpellingAccuracy = false] = varArr
            return [name, varType, description, increaseSpellingAccuracy]
          })

          if (requiredVars.length > 0) {
            node.data.extractVars = requiredVars
            setExtractVars(requiredVars)
          }

          if (extractVarSettings) {
            node.data.extractVarSettings = extractVarSettings
          }

          // Add response data with expected naming
          if (useResponseData && responseData.length > 0) {
            node.data.schedulingToolResponseData = responseData
          }

          // Add condition data (always enabled for scheduling)
          node.data.condition = condition

          // For backward compatibility, also save with original names
          node.data.url = url
          node.data.method = method
          node.data.body = body
          node.data.headers = nonEmptyHeaders.length > 0 ? nonEmptyHeaders : undefined
          node.data.responseData = useResponseData && responseData.length > 0 ? responseData : undefined

          return node
        }
        return element_node
      })

      if (!isWarnToast) {
        triggerUpdate(
          {
            nodes: updatedNodes,
            edges: updatedEdges,
          },
          false,
        )

        setIsOpen(false)
      }
      isWarnToast = false
    }
  }

  return (
    <div className="flex flex-col gap-y-2.5">
      {/* Node name field */}
      <div className="flex flex-col gap-y-1.5">
        <label>Name:</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full rounded border border-gray-300 p-1.5"
        />
      </div>

      {/* Extract variables section */}
      <ExtractInfo
        setExtractVars={setExtractVars}
        useExtractVars={useExtractVars}
        extractVars={extractVars}
        setUseExtractVars={setUseExtractVars}
        validationErrors={variableValidationErrors}
        extractVarSettings={extractVarSettings}
        setExtractVarSettings={setExtractVarSettings}
        setValidationErrors={setVariableValidationErrors}
        isWebHook
        hideDescriptions
      />

      {/* Webhook section */}
      <div className="rounded border bg-white p-2.5 shadow-xs">
        <div className="mb-2.5 flex items-center justify-between">
          <h2 className="text-base">Get Available Slots Webhook</h2>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-base"
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
        <p className="mt-0.5 text-sm text-gray-600">
          Configure webhook to fetch available appointment slots from your service.
        </p>
        {isExpanded && (
          <div className="flex flex-col gap-y-2.5">
            {/* URL and method */}
            <div className="flex items-center gap-x-2.5">
              <select
                value={method}
                onChange={e => setMethod(e.target.value)}
                className="rounded border border-gray-300 p-1.5 text-sm"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
              <input
                type="text"
                value={url}
                onChange={e => setURL(e.target.value)}
                placeholder="Enter URL"
                className="flex-1 rounded border border-gray-300 p-1.5 text-sm"
              />
            </div>

            {/* Headers section */}
            <div className="flex flex-col gap-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium">Headers</span>
                </div>
                <button
                  onClick={addHeader}
                  className="rounded bg-indigo-500 p-1.5 text-[11px] text-white"
                >
                  Add Header
                </button>
              </div>
              {headers.map((header, index) => (
                <div key={index} className="flex items-center gap-x-1.5">
                  <input
                    type="text"
                    placeholder="Key"
                    value={header[0]}
                    onChange={e =>
                      updateHeader(index, e.target.value, header[1])}
                    className={`
                      w-full rounded border border-gray-300 p-1.5 text-[11px]
                    `}
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={header[1]}
                    onChange={e =>
                      updateHeader(index, header[0], e.target.value)}
                    className={`
                      w-full rounded border border-gray-300 p-1.5 text-[11px]
                    `}
                  />
                  <button
                    onClick={() => removeHeader(index)}
                    className="rounded- p-1.5"
                  >
                    <RxCross2 color="red" />
                  </button>
                </div>
              ))}
            </div>

            {/* Body section */}
            <div className="flex flex-col gap-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Request Body (JSON)</span>
              </div>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                className={`
                  h-32 w-full rounded border border-gray-300 p-1.5 font-mono
                  text-[11px]
                `}
                placeholder="Enter request body (JSON)"
              />
            </div>

            {/* Timeout and Reroute options */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-y-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium">Timeout (seconds)</span>
                  <input
                    type="number"
                    max={999}
                    value={timeoutValue}
                    onChange={e => setTimeoutValue(Number.parseInt(e.target.value))}
                    className={`
                      w-20 rounded border border-gray-300 p-1.5 text-[11px]
                    `}
                    placeholder="Timeout"
                  />
                </div>
              </div>

              <div className="flex items-center gap-x-1.5">
                <span className="text-sm font-medium">
                  Server Reroute
                </span>
                <Switch
                  checked={useReroute}
                  onChange={setUseReroute}
                  className={`
                    ${
          useReroute ? 'bg-indigo-600' : 'bg-gray-200'
          }
                    relative inline-flex h-4 w-7 items-center rounded-full
                  `}
                >
                  <span className="sr-only">Reroute through server</span>
                  <span
                    className={`
                      ${
          useReroute ? 'translate-x-4' : 'translate-x-0.5'
          }
                      inline-block h-2.5 w-2.5 transform rounded-full bg-white
                      transition
                    `}
                  />
                </Switch>
                <div className="group relative">
                  <HelpCircle className="h-3.5 w-3.5 text-gray-400" />
                  <span className={`
                    absolute bottom-5 left-0 z-9999 hidden w-60 rounded-sm
                    bg-gray-800 px-2 py-1.5 text-2xs text-white
                    group-hover:block
                  `}
                  >
                    Reroute requests through server to avoid CORS issues
                  </span>
                </div>
              </div>
            </div>

            {/* Test API button */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={testApiRequest}
                className={`
                  flex items-center gap-1.5 rounded bg-green-500 p-1.5 text-sm
                  text-white
                `}
                disabled={isLoading}
              >
                {isLoading
                  ? 'Testing...'
                  : (
                      <>
                        <Play size={16} />
                        Test API Request
                      </>
                    )}
              </button>
            </div>

            {error && <div className="text-center text-sm text-red-500">{error}</div>}

            {/* API Response display */}
            {apiResponse && (
              <div className="mt-2.5 flex flex-col gap-y-1.5">
                <h3 className="text-base font-semibold">API Response:</h3>
                <div className={`
                  max-h-60 overflow-x-auto rounded bg-gray-100 p-2.5
                `}
                >
                  {renderJsonWithExtract(apiResponse)}
                </div>
              </div>
            )}

            {/* Response data extraction section */}
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  Required Response Variables
                </h3>
                <button
                  onClick={addResponseData}
                  className="rounded bg-indigo-500 p-1 text-[10px] text-white"
                >
                  Add Variable
                </button>
              </div>

              <div className="flex flex-col gap-y-2 border-t pt-2">
                <p className="mb-2 text-xs text-gray-600">
                  These variables must be extracted from the API response. Use the test button above to help map the JSON paths.
                </p>

                {responseData.map((item, index) => (
                  <div
                    key={index}
                    className={`
                      flex flex-col gap-y-1.5 rounded border border-gray-100
                      bg-gray-50 p-2
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        placeholder="Variable name"
                        value={item.name}
                        onChange={e => updateResponseData(index, 'name', e.target.value)}
                        className={`
                          border
                          ${['available_slots'].includes(item.name)
                    ? `border-yellow-300 bg-yellow-50`
                    : `border-gray-300`}
                          w-1/2 rounded p-1.5 text-[11px]
                        `}
                        readOnly={['available_slots'].includes(item.name)}
                      />

                      {!['available_slots'].includes(item.name) && (
                        <button onClick={() => removeResponseData(index)}>
                          <RxCross2 color="red" />
                        </button>
                      )}

                      {['available_slots'].includes(item.name) && (
                        <div className="flex items-center">
                          <span className={`
                            text-[10px] font-medium text-amber-600
                          `}
                          >
                            Required
                          </span>
                          <div className="group relative ml-1">
                            <HelpCircle className="h-3 w-3 text-amber-500" />
                            <span className={`
                              absolute right-0 bottom-5 z-9999 hidden w-40
                              rounded-sm border border-amber-200 bg-amber-50
                              px-2 py-1 text-2xs text-amber-800
                              group-hover:block
                            `}
                            >
                              This is a required variable that must be extracted from the response
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        type="text"
                        placeholder="JSONPath (e.g. $.data.slots)"
                        value={item.data}
                        onChange={e => updateResponseData(index, 'data', e.target.value)}
                        className={`
                          w-full rounded border border-gray-300 p-1.5
                          text-[11px]
                        `}
                      />
                    </div>

                    <input
                      type="text"
                      placeholder="Description (e.g. Array of available appointment dates)"
                      value={item.context}
                      onChange={e => updateResponseData(index, 'context', e.target.value)}
                      className={`
                        w-full rounded border border-gray-300 p-1.5 text-[11px]
                      `}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Condition Options */}
      <ConditionOptions
        isConditionChecked
        setIsConditionChecked={() => {}} // No-op since it's always enabled
        condition={condition}
        setCondition={setCondition}
        disabled
        disabledMessage="Condition is always enabled for scheduling nodes to ensure proper flow control."
        hardcodedPrefix={hardcodedPrefix}
      />

      {/* Save button */}
      <div className="mt-2.5 flex flex-row items-center">
        <button
          onClick={handleSave}
          className="rounded- bg-indigo-500 px-2.5 py-1.5 text-white"
        >
          Save
        </button>
      </div>
    </div>
  )
}

export default SchedulingModal
